// Audio-driven scenes (docs/scene-audio.md): one synchronized timeline per scene, with independently editable layers.
//   audio        the recordings (music, recorded dialogue, effects) and where each starts on the timeline
//   transcript   sung or spoken words with phrase (and optional word) timing -- valid without a character or a beat
//   performance  the scene's existing beats placed on the timeline (anchors); beats themselves stay unchanged
//   mix          gain envelopes and fades applied by playback, never by prompting
// Every timestamp is integer milliseconds from the scene's audio origin. `[mm:ss – mm:ss]` is only for prompts and
// people (formatAudioRange). AI composes and interprets; measured audio and deterministic playback synchronize.
import { z } from "zod";
import type { ActionCue } from "./storyActions";

const id = z.string().regex(/^[a-zA-Z0-9_-]{1,100}$/);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);

/** A scene's audio timeline is at most ten minutes. */
export const MAX_SCENE_AUDIO_MS = 600_000;
const ms = z.number().int().min(0).max(MAX_SCENE_AUDIO_MS);

/**
 *   score        background music under a finished spoken scene; dialogue and actions own the timing
 *   musical      characters perform the story through a recorded song; the recording owns the timing
 *   music-video  visuals follow a recorded song; lyrics are independent of character actions
 */
export const SCENE_AUDIO_MODES = ["score", "musical", "music-video"] as const;
export const zSceneAudioMode = z.enum(SCENE_AUDIO_MODES);
export type SceneAudioMode = z.infer<typeof zSceneAudioMode>;

/** Formats whose duration the server measures itself (the audio is never trusted for its own length). */
export const SCENE_AUDIO_MIME_TYPES = ["audio/mpeg", "audio/wav"] as const;
export const zSceneAudioMimeType = z.enum(SCENE_AUDIO_MIME_TYPES);
export type SceneAudioMimeType = z.infer<typeof zSceneAudioMimeType>;
export const MAX_SCENE_AUDIO_BYTES = 80 * 1024 * 1024;
export const MAX_SCENE_AUDIO_SINGERS = 2;

/** Progress of a scene audio job, in order. Phases a request does not need are skipped. */
export const SCENE_AUDIO_PHASES = [
  "planning",
  "speech",
  "style",
  "music",
  "analysis",
  "alignment",
  "staging",
  "validation",
] as const;
export const zSceneAudioPhase = z.enum(SCENE_AUDIO_PHASES);
export type SceneAudioPhase = z.infer<typeof zSceneAudioPhase>;

export const zSceneAudioSource = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("upload"),
    uploadedBy: id.optional(),
    originalName: z.string().max(300).optional(),
  }),
  z.object({ kind: z.literal("generated"), provider: z.string().max(60), model: z.string().max(100) }),
]);
export type SceneAudioSource = z.infer<typeof zSceneAudioSource>;

export const zSceneAudioAsset = z.object({
  id,
  role: z.enum(["music", "dialogue", "effect"]),
  /** Storage path; uploaded audio is kept unchanged at the path it was uploaded to. */
  path: z.string().min(1).max(1000),
  url: z.string().url().max(3000),
  mimeType: zSceneAudioMimeType,
  /** Measured by the server from the stored bytes. */
  durationMs: ms.positive(),
  bytes: z.number().int().positive().max(MAX_SCENE_AUDIO_BYTES),
  sha256,
  /** Where the asset starts on the scene timeline. */
  offsetMs: ms,
  source: zSceneAudioSource,
});
export type SceneAudioAsset = z.infer<typeof zSceneAudioAsset>;

/**
 * How a timestamp was established -- kept so automatic timing is never described as more exact than it is.
 *   word          recognized word boundaries (speech recognition offsets)
 *   estimate      a model's phrase-level estimate from listening to the recording
 *   interpolated  placed between better-timed neighbours
 *   manual        set or confirmed by a person in the editor
 */
export const SCENE_AUDIO_TIMING_METHODS = ["word", "estimate", "interpolated", "manual"] as const;
export const zSceneAudioTimingMethod = z.enum(SCENE_AUDIO_TIMING_METHODS);
export type SceneAudioTimingMethod = z.infer<typeof zSceneAudioTimingMethod>;
export const zSceneAudioTiming = z.object({
  method: zSceneAudioTimingMethod,
  confidence: z.number().min(0).max(1),
});
export type SceneAudioTiming = z.infer<typeof zSceneAudioTiming>;

export const zSceneAudioWord = z.object({
  text: z.string().min(1).max(100),
  startMs: ms,
  endMs: ms,
  method: zSceneAudioTimingMethod,
});
export type SceneAudioWord = z.infer<typeof zSceneAudioWord>;

/** Vocalist labels used in provider prompts; character ids and names never leave our system. */
export const zSceneAudioVocalist = z.enum(["A", "B", "both"]);
export type SceneAudioVocalist = z.infer<typeof zSceneAudioVocalist>;

/**
 * Reconciliation of the written lyrics with what the recording actually performs.
 *   matched    performed as written
 *   altered    performed with different words (intendedText keeps the written line)
 *   repeated   a written line performed again (a repeated chorus); intendedText is the line it repeats
 *   unplanned  performed but not in the written lyrics (an ad-lib, or no written lyrics were supplied)
 * Written lines that were never performed are listed in the manifest's missingLyrics, not as timed cues.
 */
export const SCENE_AUDIO_CUE_STATUSES = ["matched", "altered", "repeated", "unplanned"] as const;
export const zSceneAudioCueStatus = z.enum(SCENE_AUDIO_CUE_STATUSES);
export type SceneAudioCueStatus = z.infer<typeof zSceneAudioCueStatus>;

/** One transcript phrase. It may reference a character and a stage beat, and is valid without either. */
/**
 * A voice heard in the recording, as the listening model labelled it ("A", "B", "both" for two together, "backing",
 * "other"). Labels are guesses: one performer can be split into several (singing vs shouting), and an ad-lib can
 * look like a new singer. Who sings is decided by the singers' `voices`, never by the label alone.
 */
const voiceLabel = z.string().regex(/^[a-z0-9-]{1,20}$/i);
export const zSceneAudioVoice = z.object({
  label: voiceLabel,
  phrases: z.number().int().nonnegative(),
  sungMs: ms,
  firstMs: ms,
});
export type SceneAudioVoice = z.infer<typeof zSceneAudioVoice>;

export const zSceneAudioCue = z.object({
  id,
  startMs: ms,
  endMs: ms,
  /** The words as performed in the recording. */
  text: z.string().min(1).max(2000),
  intendedText: z.string().max(2000).optional(),
  status: zSceneAudioCueStatus,
  vocalist: zSceneAudioVocalist.optional(),
  characterId: id.optional(),
  /** The beat whose spoken turn performs this phrase. */
  beatId: id.optional(),
  /** The detected voice that sang it (see zSceneAudioVoice); `vocalist` and `characterId` follow from the singers. */
  voice: voiceLabel.optional(),
  words: z.array(zSceneAudioWord).max(80).optional(),
  timing: zSceneAudioTiming,
  /** A person confirmed this cue's timing and text. */
  reviewed: z.boolean().optional(),
});
export type SceneAudioCue = z.infer<typeof zSceneAudioCue>;

export const zSceneAudioMissingLyric = z.object({
  text: z.string().min(1).max(2000),
  /** Index of the line in the written lyrics. */
  index: z.number().int().nonnegative(),
  beatId: id.optional(),
  characterId: id.optional(),
});
export type SceneAudioMissingLyric = z.infer<typeof zSceneAudioMissingLyric>;

export const zSceneAudioSection = z.object({
  id,
  /** intro, verse, chorus, bridge, instrumental, outro... */
  label: z.string().min(1).max(60),
  startMs: ms,
  endMs: ms,
  energy: z.enum(["low", "medium", "high"]).optional(),
  description: z.string().max(500).optional(),
});
export type SceneAudioSection = z.infer<typeof zSceneAudioSection>;

export const zSceneAudioAccent = z.object({
  id,
  atMs: ms,
  kind: z.enum(["hit", "build", "drop", "break", "transition", "other"]),
  description: z.string().max(300).optional(),
});
export type SceneAudioAccent = z.infer<typeof zSceneAudioAccent>;

/** The performance layer: a beat placed on the timeline. Its group lasts until the next anchored beat. */
export const zSceneAudioAnchor = z.object({ beatId: id, atMs: ms, timing: zSceneAudioTiming });
export type SceneAudioAnchor = z.infer<typeof zSceneAudioAnchor>;

/** A singer of the recording and the character who performs it. The voice description is a snapshot. */
export const zSceneAudioSinger = z.object({
  vocalist: z.enum(["A", "B"]),
  characterId: id.optional(),
  voiceDescription: z.string().max(6000).optional(),
  /** The detected voices this singer performs. Several voices can be one singer; a voice has one singer. */
  voices: z.array(voiceLabel).max(12).optional(),
});
export type SceneAudioSinger = z.infer<typeof zSceneAudioSinger>;

export const zSceneAudioGainPoint = z.object({ atMs: ms, gain: z.number().min(0).max(2) });
export const zSceneAudioMix = z.object({
  /** Linear gain per asset, interpolated between points and held before the first and after the last. */
  envelopes: z
    .array(z.object({ assetId: id, points: z.array(zSceneAudioGainPoint).min(1).max(200) }))
    .max(16)
    .optional(),
  /** Programmed fades: `in` is silent before startMs, `out` is silent after startMs + durationMs. */
  fades: z
    .array(z.object({ assetId: id, kind: z.enum(["in", "out"]), startMs: ms, durationMs: ms }))
    .max(32)
    .optional(),
});
export type SceneAudioMix = z.infer<typeof zSceneAudioMix>;

/** Musical characteristics heard in the recording; continuity context for later scenes. */
export const zSceneAudioAnalysis = z.object({
  description: z.string().max(2000).optional(),
  tempoBpm: z.number().min(20).max(300).optional(),
  key: z.string().max(40).optional(),
  mood: z.string().max(300).optional(),
  instrumentation: z.string().max(600).optional(),
  language: z.string().max(30).optional(),
  /** The model's own count of singers: informational only (see voices). */
  vocalistCount: z.number().int().min(0).max(20).optional(),
  /** Each detected voice and how much it sings, most sung first. */
  voices: z.array(zSceneAudioVoice).max(12).optional(),
  model: z.string().max(100).optional(),
  /** Storage path of the full analysis (kept outside the scene document). */
  artifactPath: z.string().max(1000).optional(),
});
export type SceneAudioAnalysis = z.infer<typeof zSceneAudioAnalysis>;

export const SCENE_AUDIO_ISSUE_CODES = [
  "duration-mismatch",
  "asset-duplicate",
  "cue-order",
  "cue-range",
  "cue-beat",
  "cue-vocalist",
  "word-range",
  "too-many-singers",
  "extra-voices",
  "skipped-pause",
  "singer-duplicate",
  "anchor-range",
  "anchor-order",
  "anchor-unknown-beat",
  "beat-unanchored",
  "delivery-wait",
  "dialogue-overlaps-vocals",
  "speech-muted",
  "timing-uncertain",
  "missing-lyrics",
  "section-range",
  "mix-asset",
  "missing-music",
] as const;
export const zSceneAudioIssue = z.object({
  code: z.enum(SCENE_AUDIO_ISSUE_CODES),
  severity: z.enum(["error", "warning"]),
  message: z.string().max(500),
  /** The cue, beat, asset or section it is about. */
  ref: z.string().max(100).optional(),
});
export type SceneAudioIssue = z.infer<typeof zSceneAudioIssue>;

/**
 * A versioned scene audio manifest, stored as `scene.audio`. Every change writes a new `revision`; the replaced
 * manifest is archived (Performance/{p}/SceneAudioHistory/{revision}). `ready` means no validation errors.
 */
export const zSceneAudioTempo = z.object({
  bpm: z.number().min(20).max(300),
  beatsPerBar: z.number().int().min(1).max(12),
  /** Where beat 1 of bar 1 falls on the recording. */
  phaseMs: z.number().finite().min(-60000).max(60000).optional(),
  source: z.enum(["plan", "analysis"]),
});
export type SceneAudioTempo = z.infer<typeof zSceneAudioTempo>;

export const zSceneAudioManifest = z.object({
  version: z.literal(1),
  revision: id,
  previousRevision: id.optional(),
  mode: zSceneAudioMode,
  status: z.enum(["draft", "ready"]),
  durationMs: ms.positive(),
  assets: z.array(zSceneAudioAsset).min(1).max(8),
  sections: z.array(zSceneAudioSection).max(64),
  accents: z.array(zSceneAudioAccent).max(200),
  cues: z.array(zSceneAudioCue).max(300),
  missingLyrics: z.array(zSceneAudioMissingLyric).max(300),
  anchors: z.array(zSceneAudioAnchor).max(400),
  singers: z.array(zSceneAudioSinger).max(MAX_SCENE_AUDIO_SINGERS),
  mix: zSceneAudioMix.optional(),
  analysis: zSceneAudioAnalysis.optional(),
  /** The beat clock grooves and choreos follow: the song plan's tempo (bar 1 at the recording's start), else the listener's estimate. */
  tempo: zSceneAudioTempo.optional(),
  issues: z.array(zSceneAudioIssue).max(200),
  provenance: z.object({
    jobId: id,
    /** sceneBeatsHash of the beats this alignment was made against. */
    beatsHash: sha256,
    createdAt: z.number().finite().nonnegative(),
    createdBy: id.optional(),
  }),
  updatedAt: z.number().finite().nonnegative(),
});
export type SceneAudioManifest = z.infer<typeof zSceneAudioManifest>;

// ---------------------------------------------------------------------------------------------------------------
// Pure helpers shared by the backend, the player and the editor.

/** The timeline ends when its last asset does. */
export function sceneAudioDurationMs(assets: readonly Pick<SceneAudioAsset, "offsetMs" | "durationMs">[]): number {
  return assets.reduce((end, asset) => Math.max(end, asset.offsetMs + asset.durationMs), 0);
}

/** Audio owns progression: delivery never waits and the recording's position decides what is visible. */
export function sceneAudioLeadsPlayback(audio: Pick<SceneAudioManifest, "mode" | "status"> | undefined): boolean {
  return !!audio && audio.status === "ready" && (audio.mode === "musical" || audio.mode === "music-video");
}

const pad = (n: number) => String(n).padStart(2, "0");
/** `mm:ss` for prompts and people. */
export function formatAudioTime(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;
}
/** `[mm:ss – mm:ss]` for a provider prompt section or an editor label. */
export function formatAudioRange(startMs: number, endMs: number): string {
  return `[${formatAudioTime(startMs)} – ${formatAudioTime(endMs)}]`;
}

/** Index of the cue sounding at `ms` (the latest-starting one when cues overlap), or -1. Cues sorted by startMs. */
export function cueIndexAt(cues: readonly Pick<SceneAudioCue, "startMs" | "endMs">[], ms: number): number {
  let lo = 0;
  let hi = cues.length - 1;
  let last = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cues[mid].startMs <= ms) {
      last = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  for (let i = last; i >= 0 && i >= last - 8; i--) if (ms < cues[i].endMs) return i;
  return -1;
}

/** The first cue starting after `ms` that satisfies `match` (a selected role's next lyric), or -1. */
export function nextCueIndex<T extends Pick<SceneAudioCue, "startMs">>(
  cues: readonly T[],
  ms: number,
  match: (cue: T) => boolean = () => true
): number {
  for (let i = 0; i < cues.length; i++) if (cues[i].startMs > ms && match(cues[i])) return i;
  return -1;
}

/**
 * The word sounding at `ms` within a cue: recognized words when present, otherwise an even split by letters
 * (an interpolated highlight). Returns the word index and the cue's words as displayed.
 */
export function cueWordAt(
  cue: Pick<SceneAudioCue, "text" | "startMs" | "endMs" | "words">,
  ms: number
): { words: string[]; index: number } {
  if (cue.words?.length) {
    let index = -1;
    for (let i = 0; i < cue.words.length; i++) if (cue.words[i].startMs <= ms) index = i;
    return { words: cue.words.map((word) => word.text), index };
  }
  const words = cue.text.split(/\s+/).filter(Boolean);
  if (ms < cue.startMs || !words.length) return { words, index: -1 };
  const total = words.reduce((sum, word) => sum + word.length + 1, 0);
  const progress = (Math.min(ms, cue.endMs) - cue.startMs) / Math.max(1, cue.endMs - cue.startMs);
  let reached = 0;
  for (let i = 0; i < words.length; i++) {
    reached += words[i].length + 1;
    if (reached / total > progress) return { words, index: i };
  }
  return { words, index: words.length - 1 };
}

/** Linear gain of an asset at `ms`: its envelope times any programmed fades. 1 without automation. */
export function gainAt(mix: SceneAudioMix | undefined, assetId: string, ms: number): number {
  let gain = 1;
  const points = mix?.envelopes?.find((envelope) => envelope.assetId === assetId)?.points;
  if (points?.length) {
    const sorted = [...points].sort((a, b) => a.atMs - b.atMs);
    if (ms <= sorted[0].atMs) gain = sorted[0].gain;
    else if (ms >= sorted[sorted.length - 1].atMs) gain = sorted[sorted.length - 1].gain;
    else
      for (let i = 1; i < sorted.length; i++) {
        const a = sorted[i - 1];
        const b = sorted[i];
        if (ms <= b.atMs) {
          gain = a.gain + ((b.gain - a.gain) * (ms - a.atMs)) / Math.max(1, b.atMs - a.atMs);
          break;
        }
      }
  }
  for (const fade of mix?.fades ?? []) {
    if (fade.assetId !== assetId) continue;
    const t =
      fade.durationMs > 0
        ? Math.min(1, Math.max(0, (ms - fade.startMs) / fade.durationMs))
        : ms >= fade.startMs
          ? 1
          : 0;
    gain *= fade.kind === "in" ? t : 1 - t;
  }
  return Math.max(0, gain);
}

/**
 * Each beat's span on the timeline: from its anchor to the next anchored beat (or the end of the audio).
 * Beats without an anchor are absent. Anchors must be in beat order (validateSceneAudio checks this).
 */
export function beatSpans(
  anchors: readonly Pick<SceneAudioAnchor, "beatId" | "atMs">[],
  beatIds: readonly string[],
  durationMs: number
): Map<string, { startMs: number; endMs: number }> {
  const at = new Map(anchors.map((anchor) => [anchor.beatId, anchor.atMs]));
  const ordered = beatIds.filter((beatId) => at.has(beatId));
  const spans = new Map<string, { startMs: number; endMs: number }>();
  ordered.forEach((beatId, i) => {
    const startMs = at.get(beatId)!;
    const next = ordered[i + 1];
    const endMs = next === undefined ? durationMs : Math.max(startMs, at.get(next)!);
    spans.set(beatId, { startMs, endMs: Math.max(startMs, endMs) });
  });
  return spans;
}

/** Order-sensitive fingerprint input for the beats an alignment was made against (hash it with sha256). */
export function sceneBeatsFingerprint(beats: readonly Pick<ActionCue, "id" | "actions" | "interaction" | "lyricCue">[]): string {
  return JSON.stringify(
    beats.map((beat) => [
      beat.id,
      beat.actions
        .filter((action) => action.kind === "say" || action.kind === "narrate")
        .map((action) => [
          action.kind,
          action.kind === "say" ? action.character : "",
          (action as { text: string }).text,
        ]),
      !!beat.interaction,
      ...(beat.lyricCue ? [beat.lyricCue] : []),
    ])
  );
}

/** The spoken turn of a beat (at most one per beat): its text and speaker. */
export function beatSpokenLine(beat: Pick<ActionCue, "actions">): { text: string; characterId?: string } | undefined {
  const action = beat.actions.find((a) => a.kind === "say" || a.kind === "narrate");
  if (!action || (action.kind !== "say" && action.kind !== "narrate") || !action.text.trim()) return undefined;
  return { text: action.text, ...(action.kind === "say" && action.character ? { characterId: action.character } : {}) };
}

/** Voices heard, most sung first. "both" (two voices at once) is not a voice of its own. */
export function summarizeVoices(
  phrases: readonly { voice?: string; startMs: number; endMs: number }[]
): SceneAudioVoice[] {
  const voices = new Map<string, SceneAudioVoice>();
  for (const phrase of phrases) {
    if (!phrase.voice || phrase.voice === "both") continue;
    const voice = voices.get(phrase.voice) ?? { label: phrase.voice, phrases: 0, sungMs: 0, firstMs: phrase.startMs };
    voice.phrases++;
    voice.sungMs += Math.max(0, phrase.endMs - phrase.startMs);
    voice.firstMs = Math.min(voice.firstMs, phrase.startMs);
    voices.set(phrase.voice, voice);
  }
  return [...voices.values()].sort((a, b) => b.sungMs - a.sungMs || a.firstMs - b.firstMs).slice(0, 12);
}

/**
 * Who sings which detected voices. The person's choice of singers is the intent; detection only informs it.
 *   - The most-sung voices go to the chosen singers, in the order they first sing (explicit vocalists A/B are kept).
 *   - With one chosen singer, every voice is theirs: an extra "voice" is usually the same performer shouting,
 *     speaking or ad-libbing, or a backing line, and one singer was the person's intent.
 *   - With two, any further voice stays unassigned; its lines remain lyrics without a character (a warning says so).
 */
export function assignVoices(
  voices: readonly SceneAudioVoice[],
  chosen: readonly (Pick<SceneAudioSinger, "characterId" | "voiceDescription"> & { vocalist?: "A" | "B" })[]
): SceneAudioSinger[] {
  const free = (["A", "B"] as const).filter((slot) => !chosen.some((singer) => singer.vocalist === slot));
  const singers = chosen.slice(0, MAX_SCENE_AUDIO_SINGERS).map((singer) => ({
    ...singer,
    vocalist: singer.vocalist ?? free.shift()!,
    voices: [] as string[],
  }));
  const bySlot = [...singers].sort((a, b) => a.vocalist.localeCompare(b.vocalist));
  const leads = [...voices]
    .sort((a, b) => b.sungMs - a.sungMs)
    .slice(0, singers.length)
    .sort((a, b) => a.firstMs - b.firstMs);
  bySlot.forEach((singer, i) => {
    if (leads[i]) singer.voices.push(leads[i].label);
  });
  if (singers.length === 1)
    for (const voice of voices) if (!singers[0].voices.includes(voice.label)) singers[0].voices.push(voice.label);
  return singers.map(({ voices: labels, ...singer }) => ({ ...singer, voices: labels }));
}

/**
 * Each cue's vocalist and character from its detected voice and the singers. A cue sung as a beat's spoken line
 * keeps that beat's speaker. Cues from before voices were recorded are unchanged.
 */
export function attributeCues(cues: readonly SceneAudioCue[], singers: readonly SceneAudioSinger[]): SceneAudioCue[] {
  const byVoice = new Map<string, SceneAudioSinger>();
  for (const singer of singers) for (const label of singer.voices ?? []) byVoice.set(label, singer);
  return cues.map((cue) => {
    if (cue.voice === undefined) return cue;
    const { vocalist: _vocalist, characterId: beatSpeaker, ...rest } = cue;
    const singer = cue.voice === "both" ? (singers.length === 1 ? singers[0] : undefined) : byVoice.get(cue.voice);
    const vocalist = cue.voice === "both" && singers.length === 2 ? ("both" as const) : singer?.vocalist;
    const characterId = cue.beatId ? beatSpeaker : singer?.characterId;
    return { ...rest, ...(vocalist ? { vocalist } : {}), ...(characterId ? { characterId } : {}) };
  });
}

export interface SceneAudioValidationContext {
  /** Beat ids that have recorded speech clips (scene.speech). */
  recordedSpeechBeatIds?: ReadonlySet<string>;
}

const UNCERTAIN_CONFIDENCE = 0.5;

/**
 * What must hold before a scene is marked ready: cues inside the measured audio, at most two singers, every beat
 * anchored in order, no automatic delivery waits in an audio-led scene, and recorded dialogue that never sounds over
 * sung vocals. Errors keep the manifest a draft; warnings (uncertain timing, missing lyrics) are for the editor.
 */
/** The same silent response tail is used by generation, alignment and playback validation. */
export function sceneAudioResponseTail(beats: readonly Pick<ActionCue, "id" | "actions" | "interaction">[]): Set<string> {
  const tail = new Set<string>();
  for (const beat of [...beats].reverse()) {
    if (beat.interaction?.kind !== "respond" || beat.actions.length) break;
    tail.add(beat.id);
  }
  return tail;
}

/** Silent response beats at the end wait until the recording has finished. */
export function sceneAudioAnchors(
  manifest: Pick<SceneAudioManifest, "anchors" | "durationMs">,
  beats: readonly Pick<ActionCue, "id" | "actions" | "interaction">[]
): SceneAudioAnchor[] {
  const tail = sceneAudioResponseTail(beats);
  // Alignment may already have interpolated an invitation into the song. A
  // silent response always belongs after the recording, even with an old anchor.
  const anchors = manifest.anchors.filter((anchor) => !tail.has(anchor.beatId));
  for (const beat of beats)
    if (tail.has(beat.id))
      anchors.push({ beatId: beat.id, atMs: manifest.durationMs, timing: { method: "manual", confidence: 1 } });
  return anchors;
}

export function validateSceneAudio(
  manifest: Omit<SceneAudioManifest, "issues" | "status">,
  beats: readonly Pick<ActionCue, "id" | "actions" | "interaction">[],
  context: SceneAudioValidationContext = {}
): SceneAudioIssue[] {
  const issues: SceneAudioIssue[] = [];
  const add = (issue: SceneAudioIssue) => {
    if (issues.length < 200) issues.push(issue);
  };
  const end = manifest.durationMs;
  const responseTail = sceneAudioResponseTail(beats);
  const measured = sceneAudioDurationMs(manifest.assets);
  if (measured !== end)
    add({
      code: "duration-mismatch",
      severity: "error",
      message: `The timeline is ${end} ms but its audio ends at ${measured} ms.`,
    });
  const assetIds = new Set<string>();
  for (const asset of manifest.assets) {
    if (assetIds.has(asset.id))
      add({ code: "asset-duplicate", severity: "error", message: "Two audio assets share an id.", ref: asset.id });
    assetIds.add(asset.id);
  }

  const beatIds = new Set(beats.map((beat) => beat.id));
  const vocalists = new Set(manifest.singers.map((singer) => singer.vocalist));
  // The limit is on the characters who sing. How many voices the model heard is a guess (see assignVoices).
  if (manifest.singers.length > MAX_SCENE_AUDIO_SINGERS)
    add({
      code: "too-many-singers",
      severity: "error",
      message: `At most ${MAX_SCENE_AUDIO_SINGERS} characters can sing in a scene.`,
    });
  const sungBy = new Map<string, number>();
  for (const singer of manifest.singers)
    for (const label of singer.voices ?? []) sungBy.set(label, (sungBy.get(label) ?? 0) + 1);
  if ([...sungBy.values()].some((count) => count > 1))
    add({ code: "singer-duplicate", severity: "error", message: "Each voice can be sung by one character." });
  if (manifest.singers.length) {
    const unsung = manifest.cues.filter((cue) => cue.voice && cue.voice !== "both" && !sungBy.has(cue.voice));
    if (unsung.length)
      add({
        code: "extra-voices",
        severity: "warning",
        message: `${unsung.length} line${unsung.length === 1 ? " is" : "s are"} sung by ${[...new Set(unsung.map((cue) => `voice ${cue.voice}`))].join(", ")}, which no character sings. They show as lyrics only.`,
      });
  }
  if (vocalists.size !== manifest.singers.length)
    add({ code: "singer-duplicate", severity: "error", message: "Each vocalist can be assigned once." });
  const singerCharacters = manifest.singers.flatMap((singer) => (singer.characterId ? [singer.characterId] : []));
  if (new Set(singerCharacters).size !== singerCharacters.length)
    add({ code: "singer-duplicate", severity: "error", message: "A character can sing only one vocal part." });

  let previousStart = -1;
  for (const cue of manifest.cues) {
    if (cue.startMs < previousStart)
      add({ code: "cue-order", severity: "error", message: "Lyric cues must be in time order.", ref: cue.id });
    previousStart = cue.startMs;
    if (cue.endMs <= cue.startMs || cue.endMs > end)
      add({
        code: "cue-range",
        severity: "error",
        message: `${formatAudioRange(cue.startMs, cue.endMs)} is outside the ${formatAudioTime(end)} recording.`,
        ref: cue.id,
      });
    if (cue.beatId && !beatIds.has(cue.beatId))
      add({ code: "cue-beat", severity: "error", message: "A lyric cue refers to a missing beat.", ref: cue.id });
    if (cue.vocalist && cue.vocalist !== "both" && manifest.singers.length && !vocalists.has(cue.vocalist))
      add({
        code: "cue-vocalist",
        severity: "error",
        message: `Vocalist ${cue.vocalist} is not assigned.`,
        ref: cue.id,
      });
    if (cue.words?.some((word) => word.startMs < cue.startMs || word.endMs > cue.endMs || word.endMs < word.startMs))
      add({
        code: "word-range",
        severity: "warning",
        message: "Some word timings fall outside their phrase.",
        ref: cue.id,
      });
    if (!cue.reviewed && cue.timing.method !== "manual" && cue.timing.confidence < UNCERTAIN_CONFIDENCE)
      add({
        code: "timing-uncertain",
        severity: "warning",
        message: `${formatAudioRange(cue.startMs, cue.endMs)} “${cue.text.slice(0, 60)}” needs a timing check.`,
        ref: cue.id,
      });
  }
  if (manifest.missingLyrics.length)
    add({
      code: "missing-lyrics",
      severity: "warning",
      message: `${manifest.missingLyrics.length} written line${manifest.missingLyrics.length === 1 ? " is" : "s are"} not heard in the recording.`,
    });
  for (const section of manifest.sections)
    if (section.endMs <= section.startMs || section.endMs > end)
      add({
        code: "section-range",
        severity: "warning",
        message: "A section falls outside the recording.",
        ref: section.id,
      });
  for (const envelope of manifest.mix?.envelopes ?? [])
    if (!assetIds.has(envelope.assetId))
      add({
        code: "mix-asset",
        severity: "error",
        message: "A volume envelope refers to missing audio.",
        ref: envelope.assetId,
      });
  for (const fade of manifest.mix?.fades ?? [])
    if (!assetIds.has(fade.assetId))
      add({ code: "mix-asset", severity: "error", message: "A fade refers to missing audio.", ref: fade.assetId });

  const audioLed = manifest.mode === "musical" || manifest.mode === "music-video";
  if (audioLed && !manifest.assets.some((asset) => asset.role === "music"))
    add({ code: "missing-music", severity: "error", message: "An audio-led scene needs a music recording. Select or generate one in Music controls." });
  if (audioLed) {
    const anchors = new Map<string, number>();
    for (const anchor of sceneAudioAnchors(manifest, beats)) {
      if (!beatIds.has(anchor.beatId))
        add({
          code: "anchor-unknown-beat",
          severity: "error",
          message: "A timing anchor refers to a missing beat.",
          ref: anchor.beatId,
        });
      if (anchor.atMs > end || (anchor.atMs === end && !responseTail.has(anchor.beatId)))
        add({
          code: "anchor-range",
          severity: "error",
          message: "A beat starts after the recording ends.",
          ref: anchor.beatId,
        });
      anchors.set(anchor.beatId, anchor.atMs);
    }
    let previous = -1;
    for (const beat of beats) {
      const at = anchors.get(beat.id);
      if (at === undefined) {
        add({
          code: "beat-unanchored",
          severity: "error",
          message: "Every beat needs a place on the timeline.",
          ref: beat.id,
        });
        continue;
      }
      if (at < previous)
        add({
          code: "anchor-order",
          severity: "error",
          message: "Beats must keep their order on the timeline.",
          ref: beat.id,
        });
      previous = Math.max(previous, at);
      // A response invitation waits for an answer; the recording cannot. A director's question or practice pause
      // is simply not taken while the recording leads.
      if (beat.interaction?.kind === "respond" && !(responseTail.has(beat.id) && at === end))
        add({
          code: "delivery-wait",
          severity: "error",
          message: "An audio-led scene cannot stop for a response. Move the question to a silent response beat after the recording ends.",
          ref: beat.id,
        });
    }
    const pauses = beats.filter(
      (beat) => beat.interaction && (beat.interaction as { kind?: string }).kind !== "respond"
    ).length;
    if (pauses)
      add({
        code: "skipped-pause",
        severity: "warning",
        message: `${pauses} director pause${pauses === 1 ? " is" : "s are"} skipped while the recording plays.`,
      });
    const muted = beats.filter((beat) => context.recordedSpeechBeatIds?.has(beat.id)).length;
    if (muted)
      add({
        code: "speech-muted",
        severity: "warning",
        message: `${muted} recorded spoken line${muted === 1 ? " is" : "s are"} muted while the song plays.`,
      });
  }

  const vocalSpans = manifest.cues.filter((cue) => cue.vocalist !== undefined);
  for (const asset of manifest.assets) {
    if (asset.role !== "dialogue") continue;
    const from = asset.offsetMs;
    const to = asset.offsetMs + asset.durationMs;
    if (vocalSpans.some((cue) => cue.startMs < to && from < cue.endMs))
      add({
        code: "dialogue-overlaps-vocals",
        severity: "error",
        message: "A recorded spoken line would sound over the song's vocals.",
        ref: asset.id,
      });
  }
  return issues;
}

/** A manifest is ready only when it has no validation errors. */
export function sceneAudioStatus(issues: readonly SceneAudioIssue[]): SceneAudioManifest["status"] {
  return issues.some((issue) => issue.severity === "error") ? "draft" : "ready";
}

// ---------------------------------------------------------------------------------------------------------------
// manageContent `sceneAudio`: read, correct, remove, restore, or cancel a scene's audio outside the streaming create
// request. Every replaced or removed timeline is archived and can be restored.

const zSceneAudioTarget = z.object({ accountId: id, storyId: id, sceneId: id });
export const zManageSceneAudioRequest = z.discriminatedUnion("action", [
  zSceneAudioTarget.extend({ action: z.literal("status"), jobId: id.optional() }).strict(),
  zSceneAudioTarget
    .extend({
      action: z.literal("save"),
      /** The revision the edits were made against; a newer revision is a conflict. */
      expectedRevision: id,
      edits: z
        .object({
          cues: z.array(zSceneAudioCue).max(300).optional(),
          anchors: z.array(zSceneAudioAnchor).max(400).optional(),
          singers: z.array(zSceneAudioSinger).max(MAX_SCENE_AUDIO_SINGERS).optional(),
          sections: z.array(zSceneAudioSection).max(64).optional(),
          mix: zSceneAudioMix.optional(),
        })
        .strict(),
    })
    .strict(),
  zSceneAudioTarget.extend({ action: z.literal("remove"), expectedRevision: id }).strict(),
  zSceneAudioTarget
    .extend({
      action: z.literal("restore"),
      /** The archived revision to bring back (a new revision; the current one is archived in turn). */
      revision: id,
      /** The current revision, or "none" when the scene has no audio now. */
      expectedRevision: id,
    })
    .strict(),
  zSceneAudioTarget.extend({ action: z.literal("cancel"), jobId: id }).strict(),
]);
export type ManageSceneAudioRequest = z.infer<typeof zManageSceneAudioRequest>;

export type SceneAudioJobStatus = "pending" | "running" | "complete" | "failed" | "cancelled";
export interface SceneAudioJobSummary {
  id: string;
  sceneId: string;
  status: SceneAudioJobStatus;
  phase: SceneAudioPhase;
  phases: Partial<
    Record<SceneAudioPhase, { state: "started" | "done" | "skipped" | "failed"; ms?: number; message?: string }>
  >;
  error?: string;
  revision?: string;
  updatedAt: number;
}
/** An archived timeline (`Performance/{p}/SceneAudioHistory/{revision}`), newest first in `status`. */
export interface SceneAudioHistoryEntry {
  revision: string;
  archivedAt: number;
  reason: "replaced" | "edited" | "removed" | "restored";
  mode: SceneAudioMode;
  status: SceneAudioManifest["status"];
  durationMs: number;
  cueCount: number;
}
export interface ManageSceneAudioResponse {
  audio?: SceneAudioManifest | null;
  job?: SceneAudioJobSummary;
  /** status: the scene's archived timelines, newest first. */
  history?: SceneAudioHistoryEntry[];
}
